import React, {useEffect, useMemo, useRef, useState} from 'react'
import {defineMessage, useIntl, FormattedMessage} from 'react-intl'
import {useLocation, useParams} from 'react-router-dom'
import {Box, Flex, Slide, Text, useStyleConfig} from '@chakra-ui/react'
import {useCategory, useProduct} from '@salesforce/commerce-sdk-react'
import PaperlessStickyMenu from './partials/paperless-sticky-menu'
import {HTTPNotFound, HTTPError} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import Dropdown from '../../components/dropdown'
import HeadingSection from '../../components/heading-section'
import DynamicImage from '../../components/dynamic-image'
import ProductManuals from './partials/product-manuals'
import {useGetPageByType} from '../../custom-api/hooks/use-get-page-by-type'
import EditorialComponentsFactory from '../../components/editorial-components/editorial-components-factory'
import {parseAndSanitizeJSONWithSpaces} from '../../utils/utils'

import {PromoSliderVideo} from '../../components/editorial-components'
import RecommendedProducts from '../../components/recommended-products'
import SectionIconbox from '../../components/editorial-components/section-iconbox'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import FaqSection from './partials/faq-section'
import PaperlessBanner from './partials/paperless-banner'
import ProductRecall from './partials/product-recall'
import {useCustomPreferenceWithLocale} from '../../hooks/use-custom-preference'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useManuals from '../../hooks/use-manuals'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../custom-api/layers/default'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Helmet} from 'react-helmet'
import {getSites} from '../../utils/site-utils'
import AlternateMetaTag from '../../components/metatag/alternate'

const Paperless = () => {
    const styles = useStyleConfig('Paperless')
    const sliderRef = useRef(null)
    const [showMenu, setShowMenu] = useState(false)
    const {productId} = useParams()
    const {data: contentstackData} = useGetPageByType('paperless')
    const {currency: activeCurrency} = useCurrency()
    const intl = useIntl()

    const manualsRef = useRef(null)
    const registrationRef = useRef(null)
    const getStartedRef = useRef(null)
    const premiumRef = useRef(null)
    const faqRef = useRef(null)
    const accessoriesRef = useRef(null)
    const appRef = useRef(null)
    const coffeeLoungeRef = useRef(null)
    const contactUsRef = useRef(null)
    const {locale} = useMultiSite()
    const {getLanguageOptions, getProductManualsInLanguage} = useManuals()

    const premiumServicesComponent = contentstackData?.premium_services_component?.[0]
    const productRecall = useCustomPreferenceWithLocale('productRecall')

    const location = useLocation()
    const urlParams = new URLSearchParams(location.search)

    const {customParams} = getConfig()
    const currentPath = 'https://' + customParams.currentHost + location.pathname

    const {
        data: product,
        isError: isProductError,
        error: productError,
    } = useProduct({
        parameters: {
            id: urlParams.get('pid') || productId,
            currency: activeCurrency,
        },
    })
    const sites = getSites()
    const links = useMemo(() => {
        return sites
            .map((site) =>
                site.l10n.supportedLocales.map((locale) => {
                    return {
                        hreflang: locale.alias,
                        url: `/${locale.alias}/s/${product?.c_subModelIdentifier}`,
                    }
                }),
            )
            ?.flat()
    }, [])

    const isFromQr = urlParams.get('lead') === 'qr'

    const fallbackOption = {
        label: 'English (International)',
        value: {
            locale: 'en',
        },
    }

    const languageOptions = getLanguageOptions(product)

    // Compute the default language synchronously so it's available during SSR
    // (useEffect doesn't run on the server, so the old approach left selectedLanguage as null)
    const defaultLanguageOption = useMemo(() => {
        if (!languageOptions || languageOptions.length === 0) return null
        return languageOptions.find((option) => option.value.locale === locale.id) || fallbackOption
    }, [languageOptions, locale.id])

    const [selectedLanguage, setSelectedLanguage] = useState(null)
    const defaultSelected = useRef(false)

    useEffect(() => {
        if (!defaultLanguageOption) return
        if (defaultSelected.current) return

        setSelectedLanguage(defaultLanguageOption)
        defaultSelected.current = true
    }, [defaultLanguageOption])

    // Use the synchronously computed default when selectedLanguage hasn't been set yet (SSR)
    const activeLanguage = selectedLanguage || defaultLanguageOption
    const productManuals = getProductManualsInLanguage(product, activeLanguage?.value?.locale)

    if (isProductError) {
        const errorStatus = productError?.response?.status
        switch (errorStatus) {
            case 404:
                throw new HTTPNotFound('Product Not Found.')
            default:
                throw new HTTPError(`HTTP Error ${errorStatus} occurred.`)
        }
    }

    let coffeeLoungeComponent = null

    const {data: category} = useCategory({
        parameters: {
            id: product?.primaryCategoryId,
            levels: 1,
        },
    })

    if (
        product?.primaryCategoryId &&
        contentstackData?.coffee_lounge_components?.length &&
        category
    ) {
        coffeeLoungeComponent =
            contentstackData?.coffee_lounge_components.find((component) =>
                component?.categorysfcc?.data?.some((categorysfcc) =>
                    category?.parentCategoryTree?.some(
                        (parentCat) => parentCat?.id === categorysfcc?.id,
                    ),
                ),
            ) || null
    }

    let appComponent = null
    if (product?.c_hasMyCoffeeLoungeApp === 'true') {
        appComponent = contentstackData?.app_components?.my_coffee_lounge_sidecard?.[0]
    }
    if (product?.c_hasCoffeeLinkApp === 'true') {
        appComponent = contentstackData?.app_components?.coffee_link_sidecard?.[0]
    }
    if (product?.c_hasComfortApp === 'true') {
        appComponent = contentstackData?.app_components?.comfort_app_sidecard?.[0]
    }
    if (product?.c_hasNewComfortApp === 'true') {
        appComponent = contentstackData?.app_components?.new_comfort_app_sidecard?.[0]
    }

    useEffect(() => {
        if (typeof window === 'undefined') {
            return
        }

        const handleScroll = () => {
            if (
                sliderRef &&
                sliderRef.current &&
                sliderRef.current.getBoundingClientRect().top <= 0
            )
                setShowMenu(true)
            else setShowMenu(false)
        }
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const productRegSection = contentstackData?.product_registration_component?.[0]
    const contactUsSection = contentstackData?.contact_us_component?.[0]

    const displayIdentifier = isFromQr
        ? product?.c_leadIdentifier || product?.c_variantIdentifier || product?.name
        : product?.c_variantIdentifier || product?.name
    const displayNumber = isFromQr
        ? product?.c_leadNumber || product?.c_subModelIdentifier
        : product?.c_subModelIdentifier

    const shouldShowRecall = () => {
        if (!productRecall) return false

        const productVariationGroup = product?.c_subModelIdentifier

        const productSku = product?.id

        return (
            productRecall['variation group']?.includes(productVariationGroup) ||
            productRecall.sku?.includes(productSku)
        )
    }

    const recommendedProductsIds = (product?.c_recommendations || product?.recommendations || [])
        .filter((rec) => rec?.recommendationType?.value === 2)
        .map((rec) => rec.recommendedItemId)

    const sectionButtons = [
        {
            defaultMessage: 'Manuals and more',
            id: 'paperless-page.manuals-section-button',
            ref: manualsRef,
            visible: productManuals,
        },
        {
            defaultMessage: 'Register your product',
            id: 'paperless-page.product-registration-button',
            ref: registrationRef,
            visible: productRegSection,
        },
        {
            defaultMessage: 'Get started',
            id: 'paperless-page.get-started-button',
            ref: getStartedRef,
            visible: product?.c_youtubeVideoHowTo,
        },
        {
            defaultMessage: 'FAQ',
            id: 'paperless-page.faq-button',
            ref: faqRef,
            visible: !!faqRef.current,
        },
        {
            defaultMessage: 'Premium Services',
            id: 'paperless-page.premium-services-button',
            ref: premiumRef,
            visible: premiumServicesComponent,
        },
        {
            defaultMessage: 'Accessories',
            id: 'paperless-page.accessories-button',
            ref: accessoriesRef,
            visible: recommendedProductsIds.length > 0,
        },
        {
            defaultMessage: 'App',
            id: 'paperless-page.app-button',
            ref: appRef,
            visible: appComponent,
        },
        {
            defaultMessage: 'Coffee Lounge',
            id: 'paperless-page.coffee-lounge-button',
            ref: coffeeLoungeRef,
            visible: coffeeLoungeComponent,
        },
        {
            defaultMessage: 'Contact us',
            id: 'paperless-page.contact-us-button',
            ref: contactUsRef,
            visible: contactUsSection,
        },
    ]

    const paperlessHeadingTitleMessage = defineMessage({
        defaultMessage: '{productName} Support Center',
        id: 'paperless-page.heading-title',
    })

    const paperlessHeadingSubtitleMessage = defineMessage({
        defaultMessage: 'All you need to set up and get the most from your product.',
        id: 'paperless-page.heading-subtitle',
    })
    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: intl.formatMessage(paperlessHeadingTitleMessage, {
            productName: displayIdentifier,
        }),
        description: intl.formatMessage(paperlessHeadingSubtitleMessage),
        url: currentPath,
    }

    const mainImageUrl = (() => {
        const matchingImageGroup = product?.imageGroups?.find((group) =>
            group?.variationAttributes?.some(
                (attr) =>
                    attr.id === 'subModelIdentifier' &&
                    attr.values?.some((value) => value.value === product?.c_subModelIdentifier),
            ),
        )
        return (
            matchingImageGroup?.images?.[0]?.link ||
            matchingImageGroup?.images?.[0]?.disBaseLink ||
            product?.imageGroups?.[0]?.images[0]?.link ||
            product?.imageGroups?.[0]?.images[0]?.disBaseLink ||
            ''
        )
    })()

    const imageObjectSchemaMarkup = {
        '@type': 'ImageObject',
        contentUrl: mainImageUrl,
        creditText: `De'Longhi Appliances S.r.l`,
    }

    const {addLayer} = useGoogleTagManager()
    const productRegSectionButtonOnClick = async (data) => {
        addLayer(
            basic_gtm_event.bind(this, {
                event: 'generic',
                eventCategory: 'engagement',
                eventAction: 'paperless-register_product',
                eventLabel: data.label,
            }),
        )
    }

    return (
        <Flex {...styles.pageContainer} data-testid="paperless-page">
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(imageObjectSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            {product?.c_subModelIdentifier && <AlternateMetaTag links={links} />}
            <Slide
                in={showMenu}
                data-testid="paperless-sticky-menu-slider"
                aria-hidden={!showMenu}
                direction="top"
                style={{zIndex: 10, top: 0, width: '100%'}}
            >
                <PaperlessStickyMenu product={product} sectionButtons={sectionButtons} />
            </Slide>
            <HeadingSection
                title={
                    <FormattedMessage
                        {...paperlessHeadingTitleMessage}
                        values={{productName: displayIdentifier}}
                    />
                }
                description={<FormattedMessage {...paperlessHeadingSubtitleMessage} />}
                styles={styles}
                ref={sliderRef}
            />
            <Flex {...styles.productSection}>
                {product && product?.imageGroups && product?.imageGroups?.length > 0 && (
                    <Flex {...styles.productInfo} data-testid="product-info">
                        <Box {...styles.imageContainer}>
                            <DynamicImage
                                imageProps={{alt: ''}}
                                src={(() => {
                                    const matchingImageGroup = product?.imageGroups?.find((group) =>
                                        group?.variationAttributes?.some(
                                            (attr) =>
                                                attr.id === 'subModelIdentifier' &&
                                                attr.values?.some(
                                                    (value) =>
                                                        value.value ===
                                                        product?.c_subModelIdentifier,
                                                ),
                                        ),
                                    )
                                    return (
                                        matchingImageGroup?.images[0]?.link ||
                                        matchingImageGroup?.images[0]?.disBaseLink ||
                                        product?.imageGroups[0]?.images[0]?.link ||
                                        product?.imageGroups[0]?.images[0]?.disBaseLink
                                    )
                                })()}
                            />
                        </Box>
                        <Flex {...styles.productDetails}>
                            <Text {...styles.productDetailsTitle}>{displayIdentifier}</Text>
                            <Text {...styles.productDetailsId}>{displayNumber}</Text>
                        </Flex>
                    </Flex>
                )}
                <Flex {...styles.productManualsSection}>
                    <Flex {...styles.productManualsHeader}>
                        <Text {...styles.headingTitle}>
                            <FormattedMessage
                                defaultMessage="Manuals and more"
                                id="paperless-page.product-section.heading"
                            />
                        </Text>
                        <Flex {...styles.dropdownContainer}>
                            <Dropdown
                                key={locale.id} // This will reset the dropdown when the language changes
                                name=""
                                label=""
                                placeholder="Select your language"
                                isSearchable
                                value={activeLanguage}
                                options={languageOptions}
                                variant="dropdownTextStyle"
                                onChange={setSelectedLanguage}
                            />
                        </Flex>
                    </Flex>

                    {productManuals ? (
                        <Flex flexDir="column" ref={manualsRef}>
                            <ProductManuals
                                product={product}
                                productManuals={productManuals?.manuals || []}
                            />
                        </Flex>
                    ) : (
                        <Text {...styles.headingTitle} textAlign="center">
                            <FormattedMessage
                                defaultMessage="Sorry, no Manuals for this product yet"
                                id="paperless-page.no-manuals-message"
                            />
                        </Text>
                    )}
                </Flex>
            </Flex>
            {productRegSection && (
                <Flex ref={registrationRef}>
                    <SectionIconbox
                        title={productRegSection?.title}
                        cards={productRegSection?.section_items}
                        button={productRegSection?.button}
                        productRegistrationLink={`/product-registration/select?text=${displayNumber}`}
                        buttonOnClickFn={productRegSectionButtonOnClick}
                    />
                </Flex>
            )}
            {shouldShowRecall() && <ProductRecall productRecall={productRecall} />}
            {product?.c_youtubeVideoHowTo && (
                <Box ref={getStartedRef}>
                    <PromoSliderVideo
                        title={
                            <FormattedMessage
                                defaultMessage="Get Started"
                                id="paperless-page.get-started-button"
                            />
                        }
                        sliderGroup={parseAndSanitizeJSONWithSpaces(
                            product?.c_youtubeVideoHowTo,
                            product?.c_subModelIdentifier,
                        )?.map((video) => ({
                            text_block: {title: video.title},
                            video_link: {href: video.videolink},
                            thumbnail: video.medialink ? {src: video.medialink} : {},
                        }))}
                        isVariantStyle={true}
                    />
                </Box>
            )}

            <FaqSection query={product?.variationValues?.subModelIdentifier} ref={faqRef} />

            {premiumServicesComponent && (
                <Box {...styles.paperlessBannerContainer} ref={premiumRef}>
                    <PaperlessBanner
                        title={premiumServicesComponent?.title}
                        description={premiumServicesComponent?.subtitle}
                        button={premiumServicesComponent?.button}
                        image={premiumServicesComponent?.images}
                    />
                </Box>
            )}

            {recommendedProductsIds.length > 0 && (
                <Flex
                    background="background.light"
                    justifyContent="center"
                    position="relative"
                    overflow={'hidden'}
                    ref={accessoriesRef}
                >
                    <RecommendedProducts
                        products={recommendedProductsIds}
                        title={
                            <FormattedMessage
                                defaultMessage="Accessories"
                                id="paperless.recommendedproducts.title"
                            />
                        }
                        variant={'greyBackgroundVariant'}
                        tileImageVariant={'transparent'}
                        prefix="frequently-bought-together"
                        gtmCarouselName="carousel_pdp"
                    />
                </Flex>
            )}

            {appComponent && (
                <Box width="100%" ref={appRef}>
                    <EditorialComponentsFactory
                        componentType="sidecard"
                        type="full"
                        {...appComponent}
                    />
                </Box>
            )}

            {coffeeLoungeComponent && (
                <Box ref={coffeeLoungeRef}>
                    <EditorialComponentsFactory
                        componentType="coffee_lounge_component"
                        {...coffeeLoungeComponent}
                    />
                </Box>
            )}

            {contactUsSection && (
                <Box ref={contactUsRef}>
                    <SectionIconbox
                        title={contactUsSection?.title}
                        cards={[
                            {...contactUsSection.live_chat, isLiveChat: true},
                            ...(contactUsSection?.section_items || []),
                        ]}
                    />
                </Box>
            )}

            <Box>
                {contentstackData?.editorial_components.map((component) => (
                    <EditorialComponentsFactory
                        key={component.uid}
                        componentType={component?._content_type_uid}
                        {...component}
                    />
                ))}
            </Box>
        </Flex>
    )
}

Paperless.getTemplateName = () => 'paperless-page'

export default Paperless
